import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, Download, Award, Clock, Lock } from 'lucide-react';
import { toast } from 'sonner';
import OralVAEPresentation from '../OralVAEPresentation';
import { fetchSlots, rescheduleBooking, fetchBookings, createCheckout } from '../../services/api';

const OralPrepTab = ({
  currentFormula,
  entitlements,
  selectedProgramId,
  userBookings,
  rescheduleBookingId,
  setRescheduleBookingId,
  rescheduleDate,
  setRescheduleDate,
  rescheduleSlot,
  setRescheduleSlot,
  availableSlots,
  setAvailableSlots,
  setUserBookings,
  user,
  hasPresentationPersoAccess
}) => {
  return (
    <div>
      <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
        Préparation à l'oral du jury VAE
      </h2>

      {/* Introduction */}
      <div className="card accent-purple" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
        <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
          L'entretien avec le jury : une étape décisive
        </h3>
        <p className="body-medium" style={{ lineHeight: '1.8' }}>
          L'oral devant le jury est l'occasion de présenter votre parcours professionnel et de valoriser 
          vos compétences. C'est un moment d'échange où vous pourrez développer et illustrer ce que vous 
          avez écrit dans votre livret 2.
        </p>
      </div>

      {/* Documents à télécharger */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 className="heading-2" style={{ marginBottom: '1.5rem' }}>
          Supports de préparation
        </h3>
        
        {/* Présentation Interactive - bloquée pour Bronze sans achat */}
        {(currentFormula !== 'bronze' || hasPresentationPersoAccess) ? (
          <div style={{ marginBottom: '2rem' }}>
            <OralVAEPresentation />
          </div>
        ) : (
          <div data-testid="presentation-perso-paywall" style={{ textAlign: 'center', padding: '3rem 1.5rem', marginBottom: '2rem', background: 'linear-gradient(135deg, #EDE9FE, #F5F3FF)', border: '2px solid #C4B5FD', borderRadius: '1rem' }}>
            <Lock size={48} style={{ color: '#7C3AED', marginBottom: '1rem' }} />
            <h3 className="heading-2" style={{ marginBottom: '0.75rem', color: '#5B21B6' }}>
              Option Présentation personnelle
            </h3>
            <p className="body-large" style={{ marginBottom: '0.5rem', color: '#6D28D9' }}>
              Préparez votre présentation de 5-7 minutes avec Rose
            </p>
            <p className="body-small" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)', maxWidth: '500px', margin: '0 auto 1.5rem auto' }}>
              Rose vous guidera sur le fond et la forme pour convaincre le jury. Travaillez votre parcours professionnel, vos motivations et vos compétences clés.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '0.35rem 1rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600 }}>Séance individuelle</span>
              <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '0.35rem 1rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600 }}>Coaching personnalisé</span>
              <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '0.35rem 1rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600 }}>Débrief complet</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#5B21B6', marginBottom: '1.25rem' }}>
              49 €
            </div>
            <button
              className="btn-primary"
              data-testid="buy-presentation-perso"
              onClick={async () => {
                try {
                  const data = await createCheckout({
                    programId: selectedProgramId || 'deap',
                    formula: 'kit',
                    kitId: 'presentation-perso',
                    kitTitle: 'Présentation personnelle',
                    price: 49,
                    origin: window.location.origin,
                    email: user.email,
                    nom: user.name?.split(' ').slice(1).join(' ') || '',
                    prenom: user.name?.split(' ')[0] || '',
                    telephone: user.phone || user.telephone || ''
                  });
                  if (data.url) window.location.href = data.url;
                } catch (e) {
                  alert("Erreur lors de la création du paiement. Veuillez réessayer.");
                }
              }}
              style={{ fontSize: '1rem', padding: '0.85rem 2.5rem' }}
            >
              Débloquer l'accès — 49 €
            </button>
            <p className="body-small" style={{ marginTop: '1rem', color: 'var(--text-muted)' }}>
              Ou passez à la formule Argent ou Or pour l'avoir incluse.{' '}
              <Link to={`/programs/${selectedProgramId}`} style={{ color: '#7C3AED', fontWeight: 600 }}>Voir les formules</Link>
            </p>
          </div>
        )}

        {/* Mémo PDF - bloqué pour Bronze sans achat */}
        {(currentFormula !== 'bronze' || hasPresentationPersoAccess) && (
        <div 
          className="card hover-lift"
          style={{ 
            background: 'linear-gradient(135deg, #E75480 0%, #FF6B9D 100%)',
            color: 'white',
            padding: '1.5rem'
          }}
        >
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '0.75rem',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <FileText size={28} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                Mémo Oral VAE
              </h4>
              <p style={{ opacity: 0.9, fontSize: '0.875rem' }}>
                Fiche mémo avec les points essentiels à retenir pour réussir votre oral.
              </p>
            </div>
            <a
              href="/documents/memo-oral-vae.pdf"
              download="Memo-Oral-VAE.pdf"
              style={{
                background: 'white',
                color: '#E75480',
                padding: '0.75rem 1.25rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap'
              }}
              onClick={() => toast.success('Téléchargement du mémo...')}
            >
              <Download size={18} />
              Télécharger PDF
            </a>
          </div>
        </div>
        )}
      </div>

      {/* Bronze: Lien vers Oral VAE */}
      {currentFormula === 'bronze' && (
          <div className="card" style={{
            padding: '2rem', marginBottom: '2rem',
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FFFBEB 100%)',
            border: '2px solid #F59E0B30'
          }}
          data-testid="oral-prep-upgrade-prompt"
          >
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{
                background: '#F59E0B15', borderRadius: '50%', width: '52px', height: '52px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <Award size={24} color="#F59E0B" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.75rem', color: '#92400E' }}>
                  Préparez votre oral avec notre formule VAE Orale
                </h3>
                <p className="body-small" style={{ color: '#78350F', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Bénéficiez d'un accompagnement personnalisé avec Rose pour préparer votre oral :
                </p>
                <ul style={{ paddingLeft: '1.25rem', color: '#78350F', fontSize: '0.9rem', lineHeight: '2' }}>
                  <li><strong>Présentation personnelle incluse</strong></li>
                  <li><strong>Oral(aux) blanc(s)</strong> — Simulez l'oral dans les conditions réelles avec débrief complet</li>
                </ul>
                <Link
                  to="/oral-vae"
                  className="btn-primary"
                  style={{ textDecoration: 'none', marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  data-testid="oral-prep-upgrade-btn"
                >
                  Découvrir la formule VAE Orale
                </Link>
              </div>
            </div>
          </div>
      )}

      {/* Silver/Gold: Séances présentation personnelle + oraux blancs */}
      {(currentFormula === 'gold' || currentFormula === 'silver') && (
        <>
          {/* Présentation personnelle */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              Travaillez votre présentation personnelle
            </h3>
            <div className="card" style={{
              padding: '2rem',
              background: 'linear-gradient(135deg, #EDE9FE 0%, #fff 100%)',
              border: '2px solid #8B5CF630'
            }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1.25rem' }}>
                <div style={{
                  background: '#8B5CF615', borderRadius: '50%', width: '52px', height: '52px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Award size={24} color="#8B5CF6" />
                </div>
                <div>
                  <h4 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.25rem' }}>Séance avec Rose</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Préparez et répétez votre présentation de 5-7 minutes avec Rose. Elle vous guidera sur le fond et la forme pour convaincre le jury.
                  </p>
                </div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: '#8B5CF615', padding: '0.4rem 1rem', borderRadius: '2rem',
                color: '#7C3AED', fontWeight: 600, fontSize: '0.8rem', marginBottom: '1.25rem'
              }}>
                <CheckCircle size={16} /> {currentFormula === 'gold' ? 'Inclus dans votre formule Or' : `${entitlements.presentationPerso} séance incluse dans votre formule Argent`}
              </div>
              <br />
              <Link to={`/booking?direct=true&program=${selectedProgramId}&formula=${currentFormula}&type=presentation`} className="btn-primary" style={{ textDecoration: 'none' }} data-testid="book-presentation-perso">
                Réserver une séance présentation
              </Link>
            </div>
          </div>

          {/* Oraux blancs */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {currentFormula === 'gold' ? 'Vos 3 oraux blancs de préparation' : 'Votre oral blanc de préparation'}
            </h3>
            <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
              Simulez l'oral dans les conditions réelles. Rose joue le rôle du jury et vous fait un débrief complet après chaque session.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {Array.from({ length: entitlements.oralBlanc }, (_, i) => i + 1).map(num => {
                const oralBooking = userBookings.find(b => b.notes && b.notes.includes(`Oral blanc ${num}`));
                return (
                  <div key={num} className="card" style={{ padding: '1.25rem' }} data-testid={`oral-blanc-${num}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                          background: oralBooking ? '#10B98115' : '#F3F4F6',
                          borderRadius: '50%', width: '42px', height: '42px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          border: oralBooking ? '2px solid #10B981' : '2px solid #D1D5DB'
                        }}>
                          <span style={{ fontWeight: 800, fontSize: '1rem', color: oralBooking ? '#059669' : '#6B7280' }}>{num}</span>
                        </div>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Oral blanc {entitlements.oralBlanc > 1 ? `n°${num}` : ''}</span>
                          {oralBooking ? (
                            <span className="body-small" style={{ display: 'block', color: '#059669', marginTop: '0.15rem' }}>
                              {new Date(oralBooking.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {oralBooking.time}
                            </span>
                          ) : (
                            <span className="body-small" style={{ display: 'block', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                              Pas encore planifié
                            </span>
                          )}
                        </div>
                      </div>
                      {oralBooking ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{
                            background: '#ECFDF5', color: '#059669',
                            padding: '0.25rem 0.75rem', borderRadius: '1rem',
                            fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase'
                          }}>Planifié</span>
                          <button
                            className="btn-secondary"
                            style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}
                            onClick={() => {
                              if (rescheduleBookingId === oralBooking.bookingId) {
                                setRescheduleBookingId(null); setRescheduleDate(null); setRescheduleSlot(null);
                              } else {
                                setRescheduleBookingId(oralBooking.bookingId); setRescheduleDate(null); setRescheduleSlot(null);
                                fetchSlots().then(setAvailableSlots).catch(console.error);
                              }
                            }}
                          >
                            {rescheduleBookingId === oralBooking?.bookingId ? 'Annuler' : 'Modifier'}
                          </button>
                        </div>
                      ) : (
                        <Link 
                          to={`/booking?direct=true&program=${selectedProgramId}&formula=${currentFormula}&type=oral-blanc`}
                          className="btn-primary"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', textDecoration: 'none' }}
                          data-testid={`book-oral-blanc-${num}`}
                        >
                          Planifier
                        </Link>
                      )}
                    </div>

                    {/* Reschedule form for oral blanc */}
                    {oralBooking && rescheduleBookingId === oralBooking.bookingId && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-section)', borderRadius: '0.75rem' }}>
                        <p className="body-small" style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Nouvelle date :</p>
                        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                          <input type="date" min={new Date(Date.now() + 2 * 86400000).toISOString().split('T')[0]}
                            value={rescheduleDate || ''} onChange={(e) => { setRescheduleDate(e.target.value); setRescheduleSlot(null); }}
                            style={{ padding: '0.6rem 1rem', borderRadius: '0.5rem', border: '2px solid var(--border-primary)', background: 'var(--bg-card)', fontSize: '0.85rem' }}
                          />
                          {rescheduleDate && (
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              {availableSlots.filter(s => s.date === rescheduleDate && s.available).map(slot => (
                                <button key={slot.id} onClick={() => setRescheduleSlot(slot)}
                                  className={rescheduleSlot?.id === slot.id ? 'btn-primary' : 'btn-secondary'}
                                  style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                                  <Clock size={14} style={{ marginRight: '0.25rem' }} /> {slot.time}
                                </button>
                              ))}
                              {availableSlots.filter(s => s.date === rescheduleDate && s.available).length === 0 && (
                                <p className="body-small" style={{ color: 'var(--text-muted)' }}>Aucun créneau.</p>
                              )}
                            </div>
                          )}
                        </div>
                        {rescheduleSlot && (
                          <button className="btn-primary" style={{ marginTop: '1rem', fontSize: '0.85rem' }}
                            onClick={async () => {
                              try {
                                await rescheduleBooking(oralBooking.bookingId, rescheduleDate, rescheduleSlot.time);
                                toast.success('Oral blanc modifié !');
                                setRescheduleBookingId(null); setRescheduleDate(null); setRescheduleSlot(null);
                                fetchBookings(user.id).then(setUserBookings).catch(() => {});
                              } catch (e) { toast.error(e.message || 'Erreur'); }
                            }}>
                            Confirmer
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Key Points */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 className="heading-2" style={{ marginBottom: '1.5rem' }}>
          Points clés à maîtriser
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          <div className="card accent-blue hover-lift">
            <h4 className="heading-3" style={{ marginBottom: '0.75rem' }}>
              Présentation personnelle
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                Préparez une présentation de 5-7 minutes
              </li>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                Mettez en avant votre parcours professionnel
              </li>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                Expliquez vos motivations pour la VAE
              </li>
            </ul>
          </div>

          <div className="card accent-orange hover-lift">
            <h4 className="heading-3" style={{ marginBottom: '0.75rem' }}>
              Illustrations concrètes
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                Préparez 3-4 exemples de situations vécues
              </li>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                Utilisez la méthode STAR (Situation, Tâche, Action, Résultat)
              </li>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                Soyez précis et factuel
              </li>
            </ul>
          </div>

          <div className="card accent-green hover-lift">
            <h4 className="heading-3" style={{ marginBottom: '0.75rem' }}>
              Questions fréquentes
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                "Pourquoi cette VAE maintenant ?"
              </li>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                "Décrivez une situation difficile"
              </li>
              <li className="body-small" style={{ marginBottom: '0.5rem', paddingLeft: '1rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                "Quels sont vos axes de progression ?"
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
          Conseils pratiques
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <CheckCircle size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
            <div>
              <p className="body-medium" style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                Entraînez-vous à l'oral
              </p>
              <p className="body-small">
                Répétez votre présentation devant un proche ou enregistrez-vous pour identifier vos points à améliorer
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <CheckCircle size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
            <div>
              <p className="body-medium" style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                Maîtrisez votre livret 2
              </p>
              <p className="body-small">
                Relisez-le plusieurs fois, le jury peut vous interroger sur n'importe quel point
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <CheckCircle size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
            <div>
              <p className="body-medium" style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                Soignez votre communication non-verbale
              </p>
              <p className="body-small">
                Maintenez le contact visuel, adoptez une posture ouverte et souriez naturellement
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <CheckCircle size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
            <div>
              <p className="body-medium" style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                Préparez vos supports
              </p>
              <p className="body-small">
                Si autorisé, préparez quelques documents visuels (photos, schémas) pour illustrer vos propos
              </p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <Link to={`/booking?direct=true&program=${selectedProgramId}&formula=${currentFormula}&type=oral-blanc`} className="btn-primary" style={{ textDecoration: 'none' }}>
          Réserver une séance de simulation d'oral
        </Link>
      </div>
    </div>
  );
};

export default OralPrepTab;
